/**
 * Reasoning Sidebar Design System Tokens
 *
 * Centralized design tokens for reasoning sidebar styling.
 * Based on Figma design: node-id=2643-2711
 *
 * These tokens ensure:
 * - Consistent styling across all reasoning step renderers
 * - Easy theming and design system evolution
 * - Single source of truth for visual properties
 */

export const REASONING_DESIGN_TOKENS = {
  /**
   * Typography scales
   */
  typography: {
    /** Section title (design-system text-base / 16px, semibold) */
    sectionTitle: 'text-base font-semibold',
    /** Status subtitle (text-sm / 14px, italic) */
    statusSubtitle: 'text-sm italic',
    /** Content text (text-sm / 14px, regular) */
    contentText: 'text-sm font-normal',
    /** Sub-item text (text-sm / 14px) */
    subItem: 'text-sm',
  },

  /**
   * Color palette
   */
  colors: {
    /** Section title color */
    sectionTitle: 'text-fg-primary',
    /** Status subtitle color */
    statusSubtitle: 'text-fg-primary',
    /** Content text color */
    contentText: 'text-fg-primary',
    /** Timeline connector line */
    timelineLine: 'bg-fg-primary/30',
    /** Icon color */
    iconColor: 'text-fg-primary',
    /** Header background */
    headerBg: 'bg-bg-secondary',
    /** Header title color (Gray blue/700) */
    headerTitle: 'text-fg-secondary',
  },

  /**
   * Spacing and sizing
   */
  spacing: {
    /** Main step icon size (22-27px based on icon) */
    iconSize: {
      main: 'size-[24px]',
      /** Sub-item icon size (18px) */
      sub: 'size-[18px]',
    },
    /** Vertical entity line positioning */
    verticalLine: {
      /** Line width */
      width: 'w-px',
      /** Left position from container edge */
      left: 'left-[12px]',
    },
    /** Gap between icon and content */
    iconGap: 'gap-3',
    /** Padding for step content */
    stepPadding: 'py-3',
  },

  /**
   * Layout properties
   */
  layout: {
    /** Step container */
    stepContainer: 'flex items-start gap-3',
    /** Content area */
    contentArea: 'flex-1 min-w-0',
    /** Header row (title + chevron) */
    headerRow: 'flex items-center justify-between gap-2',
  },

  /**
   * Interactive states
   */
  states: {
    /** Chevron rotation when expanded */
    chevronExpanded: 'rotate-180',
    /** Hover state for clickable areas */
    hoverBg: 'hover:bg-bg-secondary/50',
  },

  /**
   * Inline card styling (for chat messages)
   * Based on Figma: node-id=2644-3010
   */
  inlineCard: {
    /** Outer card background */
    outerBg: 'bg-bg-secondary',
    /** Outer card border */
    outerBorder: 'border border-border-secondary',
    /** Inner card background */
    innerBg: 'bg-bg-primary',
    /** Inner card border */
    innerBorder: 'border border-border-secondary',
    /** Card border radius */
    radius: 'rounded-[5px]',
    /** Status header text */
    statusText: 'text-sm font-medium text-fg-primary',
    /** Status icon size */
    statusIconSize: 'w-[22px] h-[22px]',
    /** Spinner icon size */
    spinnerSize: 'w-6 h-6',
  },
} as const;

/**
 * Stage icon mappings
 * Maps reasoning stages to their display properties
 */
export const REASONING_STAGE_CONFIG: Record<
  string,
  {
    icon: string;
    displayName: string;
    statusText: string;
  }
> = {
  thinking: {
    icon: 'lightbulb',
    displayName: 'Thinking',
    statusText: 'Ahoy is analyzing/reasoning',
  },
  research: {
    icon: 'search',
    displayName: 'Research',
    statusText: 'Ahoy is gathering info',
  },
  planning: {
    icon: 'clipboard',
    displayName: 'Planning',
    statusText: 'Ahoy is creating a plan',
  },
  action: {
    icon: 'magic-wand',
    displayName: 'Action',
    statusText: 'Ahoy is creating actions and artifacts',
  },
  analysis: {
    icon: 'sparkle',
    displayName: 'Analysis',
    statusText: 'Ahoy is analyzing results',
  },
  // Fallback for unknown stages
  default: {
    icon: 'lightbulb',
    displayName: 'Thinking',
    statusText: 'Ahoy is thinking',
  },
};

/**
 * Get stage configuration with fallback
 */
export function getStageConfig(stage: string) {
  const normalizedStage = stage.toLowerCase().trim();
  return (
    REASONING_STAGE_CONFIG[normalizedStage] || REASONING_STAGE_CONFIG.default
  );
}

/**
 * Whether a reasoning step is currently "in progress" (the live cue: shimmering
 * label + status text). The streaming progress endpoint marks the in-flight step
 * "processing" (apps/reasoning/viewsets/conversations.py), while other paths may
 * use "active" — treat both as live so the shimmer fires during streaming.
 */
export function isStepInProgress(status: string): boolean {
  return status === 'active' || status === 'processing';
}

/**
 * Tool configuration
 * Maps tool names to their display properties
 */
export const TOOL_CONFIG: Record<
  string,
  {
    icon: string;
    displayName: string;
    category:
      | 'communication'
      | 'calendar'
      | 'research'
      | 'proposal'
      | 'crm'
      | 'learning'
      | 'other';
  }
> = {
  // Communication tools
  gmail_search: {
    icon: 'mail-search',
    displayName: 'Gmail Search',
    category: 'communication',
  },
  gmail_read: {
    icon: 'mail-open',
    displayName: 'Read Email',
    category: 'communication',
  },
  gmail_thread: {
    icon: 'mail',
    displayName: 'Email Thread',
    category: 'communication',
  },
  // Outlook tools
  outlook_search: {
    icon: 'mail-search',
    displayName: 'Outlook Search',
    category: 'communication',
  },
  outlook_read: {
    icon: 'mail-open',
    displayName: 'Read Email',
    category: 'communication',
  },
  outlook_thread: {
    icon: 'mail',
    displayName: 'Email Thread',
    category: 'communication',
  },
  // Calendar tools
  get_calendar_events: {
    icon: 'calendar',
    displayName: 'Get Calendar Events',
    category: 'calendar',
  },
  create_google_meet: {
    icon: 'video',
    displayName: 'Create Google Meet',
    category: 'calendar',
  },
  // Teams tools
  teams_get_details: {
    icon: 'video',
    displayName: 'Teams Meeting Details',
    category: 'calendar',
  },
  teams_list_upcoming: {
    icon: 'calendar',
    displayName: 'List Teams Meetings',
    category: 'calendar',
  },
  // Research tools
  research_web: {
    icon: 'globe',
    displayName: 'Web Research',
    category: 'research',
  },
  // Action tools
  create_proposal: {
    icon: 'file-plus',
    displayName: 'Create Action',
    category: 'proposal',
  },
  update_proposed_action: {
    icon: 'edit',
    displayName: 'Update Action',
    category: 'proposal',
  },
  withdraw_proposal: {
    icon: 'x-circle',
    displayName: 'Withdraw Action',
    category: 'proposal',
  },
  get_proposal: {
    icon: 'file',
    displayName: 'Get Action',
    category: 'proposal',
  },
  list_proposals: {
    icon: 'list',
    displayName: 'List Actions',
    category: 'proposal',
  },
  // CRM tools
  search_crm: {
    icon: 'search',
    displayName: 'Search CRM',
    category: 'crm',
  },
  propose_crm_changes: {
    icon: 'database',
    displayName: 'Suggest CRM Changes',
    category: 'crm',
  },
  propose_property_updates: {
    icon: 'edit-3',
    displayName: 'Suggest Property Updates',
    category: 'crm',
  },
  // Learning tools
  get_user_context: {
    icon: 'user',
    displayName: 'Get User Context',
    category: 'learning',
  },
  update_user_context: {
    icon: 'user-plus',
    displayName: 'Update User Context',
    category: 'learning',
  },
  get_team_context: {
    icon: 'users',
    displayName: 'Get Team Context',
    category: 'learning',
  },
  update_team_context: {
    icon: 'users-plus',
    displayName: 'Update Team Context',
    category: 'learning',
  },
  // Granola meeting tools
  granola_query_granola_meetings: {
    icon: 'calendar',
    displayName: 'Query Meetings',
    category: 'calendar',
  },
  granola_list_meetings: {
    icon: 'calendar',
    displayName: 'List Meetings',
    category: 'calendar',
  },
  granola_list_meeting_folders: {
    icon: 'calendar',
    displayName: 'List Meeting Folders',
    category: 'calendar',
  },
  granola_get_meetings: {
    icon: 'calendar',
    displayName: 'Get Meetings',
    category: 'calendar',
  },
  granola_get_meeting_transcript: {
    icon: 'calendar',
    displayName: 'Get Meeting Transcript',
    category: 'calendar',
  },
  // Dynamic views
  create_dynamic_view: {
    icon: 'layout',
    displayName: 'Create View',
    category: 'other',
  },
};

export interface ToolResult {
  tool_name: string;
  tool_input: Record<string, unknown>;
  result: unknown;
  error?: string;
  // Per-tool execution status for in-flight animation.
  // Missing status is treated as completed (legacy/historical data).
  status?: 'running' | 'done' | 'error';
}

/**
 * Get tool configuration with fallback
 */
export function getToolConfig(toolName: string) {
  return (
    TOOL_CONFIG[toolName] || {
      icon: 'tool',
      displayName: toolName
        .replace(/_/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase()),
      category: 'other' as const,
    }
  );
}
